import React, { useState } from 'react';
import { Upload, message, Button, Space } from 'antd';
import {
  InboxOutlined,
  LoadingOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useSession } from 'next-auth/react';
import {
  useCreateOneUserUploadedAssetsMutation,
  FindManyUserUploadedAssetsDocument,
  GetBoughtAssetsByUserDocument,
} from '@src/components/generated/graphql';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import styles from './npc-model-with-preview-uploader.module.css';

const { Dragger } = Upload;

const MESSAGES = {
  MODEL_UPLOAD_SUCCESS: 'Model uploaded successfully!',
  MODEL_UPLOAD_ERROR: 'Failed to upload model. Please try again.',
  PREVIEW_UPLOAD_SUCCESS: 'Preview uploaded successfully!',
  PREVIEW_UPLOAD_ERROR: 'Failed to upload preview. Please try again.',
  INVALID_MODEL_TYPE: 'Please upload a valid GLB file',
  INVALID_PREVIEW_TYPE:
    'Please upload a valid image file (JPG, PNG, GIF, WebP)',
  FILE_TOO_LARGE: 'File size must be less than 10MB',
  NOT_SIGNED_IN: 'Please sign in to upload files',
  UPLOAD_COMPLETE: 'NPC uploaded successfully with preview!',
  UPLOAD_ERROR: 'Failed to upload NPC. Please try again.',
} as const;

const UPLOAD_BUTTON_TEXT = {
  MODEL_DEFAULT: 'Click or drag GLB model to upload',
  MODEL_UPLOADING: 'Uploading model...',
  PREVIEW_DEFAULT: 'Click or drag preview image to upload',
  PREVIEW_UPLOADING: 'Uploading preview...',
  UPLOAD_NPC: 'Upload NPC',
  UPLOADING_NPC: 'Uploading NPC...',
} as const;

const MODEL_EXTENSIONS = ['glb'] as const;
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'] as const;
const ACCEPTED_MODEL_EXTENSIONS = '.glb';
const ACCEPTED_IMAGE_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

type ModelExtension = typeof MODEL_EXTENSIONS[number];
type ImageExtension = typeof IMAGE_EXTENSIONS[number];

const getFileExtension = (filename: string): string | undefined => {
  return filename.split('.').pop()?.toLowerCase();
};

const isValidModelFile = (file: File): boolean => {
  const extension = getFileExtension(file.name);
  return extension
    ? MODEL_EXTENSIONS.includes(extension as ModelExtension)
    : false;
};

const isValidImageFile = (file: File): boolean => {
  const extension = getFileExtension(file.name);
  return extension
    ? IMAGE_EXTENSIONS.includes(extension as ImageExtension)
    : false;
};

const isFileSizeValid = (file: File): boolean => {
  return file.size <= MAX_FILE_SIZE;
};

const uploadModel = async (file: File, userEmail: string): Promise<string> => {
  try {
    const fileName = `npc-model-${Date.now()}-${file.name}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);
    formData.append('email', userEmail);
    formData.append('fileType', 'npc-model');

    const response = await fetch('/api/upload-file-s3', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();

    if (!result?.status) {
      throw new Error('Upload failed - API returned error status');
    }

    const CDN_URL = process.env.NEXT_PUBLIC_ASSETS_IMAGES_URL;
    if (!CDN_URL) {
      throw new Error('CDN URL is not configured');
    }

    const modelUrl = `${CDN_URL}/${encodeURIComponent(
      userEmail
    )}/npc-model/${encodeURIComponent(fileName)}`;
    return modelUrl;
  } catch (error) {
    console.error('Model upload error:', error);
    throw new Error(MESSAGES.MODEL_UPLOAD_ERROR);
  }
};

const uploadPreviewImage = async (
  file: File,
  userEmail: string
): Promise<string> => {
  try {
    const fileName = `npc-preview-${Date.now()}-${file.name}`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', fileName);
    formData.append('email', userEmail);
    formData.append('fileType', 'npc-preview');

    const response = await fetch('/api/upload-file-s3', {
      method: 'POST',
      body: formData,
    });

    const result = await response.json();

    if (!result?.status) {
      throw new Error('Upload failed - API returned error status');
    }

    const CDN_URL = process.env.NEXT_PUBLIC_ASSETS_IMAGES_URL;
    if (!CDN_URL) {
      throw new Error('CDN URL is not configured');
    }

    const imageUrl = `${CDN_URL}/${encodeURIComponent(
      userEmail
    )}/npc-preview/${encodeURIComponent(fileName)}`;
    return imageUrl;
  } catch (error) {
    console.error('Preview image upload error:', error);
    throw new Error(MESSAGES.PREVIEW_UPLOAD_ERROR);
  }
};

interface NPCModelWithPreviewUploaderProps {
  onNPCUploaded: (
    inventoryItemId: string,
    url: string,
    previewUrl: string,
    name: string
  ) => Promise<void>;
  disabled?: boolean;
}

const NPCModelWithPreviewUploader: React.FC<
  NPCModelWithPreviewUploaderProps
> = ({ onNPCUploaded, disabled = false }) => {
  const { data: session } = useSession();
  const [createInventoryItem] = useCreateOneUserUploadedAssetsMutation();

  const [modelFile, setModelFile] = useState<File | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [modelLoading, setModelLoading] = useState<boolean>(false);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);

  const beforeModelUpload = (file: File): boolean => {
    if (!isValidModelFile(file)) {
      message.error(MESSAGES.INVALID_MODEL_TYPE);
      return false;
    }

    if (!isFileSizeValid(file)) {
      message.error(MESSAGES.FILE_TOO_LARGE);
      return false;
    }

    setModelFile(file);
    return false;
  };

  const beforePreviewUpload = (file: File): boolean => {
    if (!isValidImageFile(file)) {
      message.error(MESSAGES.INVALID_PREVIEW_TYPE);
      return false;
    }

    if (!isFileSizeValid(file)) {
      message.error(MESSAGES.FILE_TOO_LARGE);
      return false;
    }

    setPreviewFile(file);
    return false;
  };

  const handleUploadNPC = async (): Promise<void> => {
    if (!session?.user?.email || !session?.user?.id) {
      message.error(MESSAGES.NOT_SIGNED_IN);
      return;
    }

    if (!modelFile) {
      message.error('Please select a model file first');
      return;
    }

    if (!previewFile) {
      message.error('Please select a preview image first');
      return;
    }

    setUploading(true);

    try {
      setModelLoading(true);
      const modelUrl = await uploadModel(modelFile, session.user.email);
      setModelLoading(false);

      setPreviewLoading(true);
      const previewUrl = await uploadPreviewImage(
        previewFile,
        session.user.email
      );
      setPreviewLoading(false);

      const assetName = modelFile.name.split('.')[0];
      const res = await createInventoryItem({
        variables: {
          data: {
            assetType: ASSET_TYPE.GLTF.OTHER as any,
            assetUrl: modelUrl,
            name: assetName,
            userId: session.user.id,
            previewImage: previewUrl,
          },
        },
        refetchQueries: [
          FindManyUserUploadedAssetsDocument,
          GetBoughtAssetsByUserDocument,
        ],
      });
      if (!res || !res.data) {
        throw new Error('Failed to create inventory item.');
      }
      setModelFile(null);
      setPreviewFile(null);

      await onNPCUploaded(
        res.data.createOneUserUploadedAssets.id,
        res.data.createOneUserUploadedAssets.assetUrl,
        previewUrl,
        assetName
      );
      message.success(MESSAGES.UPLOAD_COMPLETE);
    } catch (error) {
      console.error('NPC upload error:', error);
      message.error(MESSAGES.UPLOAD_ERROR);
    } finally {
      setUploading(false);
      setModelLoading(false);
      setPreviewLoading(false);
    }
  };

  const modelUploadButton = (
    <div className={styles.uploadContent}>
      <p className='ant-upload-drag-icon'>
        {modelLoading ? <LoadingOutlined /> : <InboxOutlined />}
      </p>
      <p className='ant-upload-text'>
        {modelLoading
          ? UPLOAD_BUTTON_TEXT.MODEL_UPLOADING
          : UPLOAD_BUTTON_TEXT.MODEL_DEFAULT}
      </p>
      <p className='ant-upload-hint'>Support for GLB files (max 10MB)</p>
    </div>
  );

  const previewUploadButton = (
    <div className={styles.uploadContent}>
      <p className='ant-upload-drag-icon'>
        {previewLoading ? <LoadingOutlined /> : <InboxOutlined />}
      </p>
      <p className='ant-upload-text'>
        {previewLoading
          ? UPLOAD_BUTTON_TEXT.PREVIEW_UPLOADING
          : UPLOAD_BUTTON_TEXT.PREVIEW_DEFAULT}
      </p>
      <p className='ant-upload-hint'>
        Support for JPG, PNG, GIF, WebP (max 10MB)
      </p>
    </div>
  );

  const canUpload = modelFile && previewFile && !uploading;

  return (
    <div className={styles.npcModelWithPreviewUploader}>
      <Space direction='vertical' style={{ width: '100%' }}>
        <div className={styles.uploaderSection}>
          <h4 className={styles.sectionTitle}>1. Upload 3D Model</h4>
          <Dragger
            name='modelFile'
            multiple={false}
            beforeUpload={beforeModelUpload}
            disabled={disabled || uploading}
            showUploadList={false}
            accept={ACCEPTED_MODEL_EXTENSIONS}
            className={styles.dragger}
          >
            {modelUploadButton}
          </Dragger>
          {modelFile && (
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>✓ {modelFile.name}</span>
            </div>
          )}
        </div>

        <div className={styles.uploaderSection}>
          <h4 className={styles.sectionTitle}>2. Upload Preview Image</h4>
          <Dragger
            name='previewFile'
            multiple={false}
            beforeUpload={beforePreviewUpload}
            disabled={disabled || uploading || !modelFile}
            showUploadList={false}
            accept={ACCEPTED_IMAGE_EXTENSIONS}
            className={styles.dragger}
          >
            {previewUploadButton}
          </Dragger>
          {previewFile && (
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>✓ {previewFile.name}</span>
            </div>
          )}
        </div>

        <div className={styles.uploadButtonSection}>
          <Button
            type='primary'
            icon={<UploadOutlined />}
            onClick={handleUploadNPC}
            disabled={!canUpload}
            loading={uploading}
            size='large'
            className={styles.uploadButton}
          >
            {uploading
              ? UPLOAD_BUTTON_TEXT.UPLOADING_NPC
              : UPLOAD_BUTTON_TEXT.UPLOAD_NPC}
          </Button>
        </div>
      </Space>
    </div>
  );
};

export default NPCModelWithPreviewUploader;
